import React, { FC, Suspense, useMemo, useContext } from "react";
import { ErrorBoundary } from "react-error-boundary";

import TaskAttachmentDropzone from "./TaskAttachmentDropzone";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { TaskSave } from "@/features/task/model/Task/TaskRequest";
import CustomCheckbox from "@/shared/forms/CustomCheckbox";
import CustomTextAreaArray from "@/shared/forms/CustomTextareaArray";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertToast } from "@/shared/notifications/AlertToast";
import { CloseActionButton, SaveActionButton } from "@/shared/ui";
import ComponentError from "@/shared/ui/errors/Components";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Form, Formik, FormikHelpers } from "formik";
import * as yup from "yup";

import { useCreateTask } from "@/service/TaskQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);

/* ─────────────────── Types ─────────────────── */
interface Option {
	Id: number;
	Name: string;
}

// Define a minimal TaskPayload so `initialData` is typed and safe.
type TaskPayload = {
	title: string;
	description: string;
	deadline: string;
	showBefore: string;
	recurringFrequency: string;
	system: number;
	handover: number | boolean;
	shiftlog: number | boolean;
	tags: string[];
	groupsResponsible: number[];
	usersResponsible: number[];
};

export interface AddNewTaskDialogProps {
	open: boolean;
	handleClose: () => void;
	title: string;
	groupOptions: Option[];
	userOptions: Option[];
	initialData?: Partial<TaskPayload>;
	systemOptions: Option[];
	enforceFutureDeadline?: boolean;
	groupsNewTask?: string[];
	setGroupsNewTask?: React.Dispatch<React.SetStateAction<string[]>>;
}

interface FormValues {
	title: string;
	description: string;
	deadline: string;
	showBefore: string;
	frequency: string;
	system: number;
	handover: boolean;
	shiftlog: boolean;
	tags: string[];
	selectedGroups: Option[];
	selectedUsers: Option[];
	fileName?: string;
	file?: File | null;
}

const toErrorText = (value: unknown): string | undefined =>
	typeof value === "string" ? value : undefined;

const toIdList = (value: unknown): number[] => {
	if (typeof value === "string") {
		return value
			.split(",")
			.map((v) => Number(v))
			.filter((v) => Number.isFinite(v));
	}
	if (Array.isArray(value)) {
		return value.map((v) => Number(v)).filter((v) => Number.isFinite(v));
	}
	return [];
};

/* ─────────────────── UI Tokens (pastel & rounded) ─────────────────── */
const COL = {
	chipBg: "#EEF2FF",
	chipText: "#3730A3",
};

const roundedInput = {
	"& .MuiOutlinedInput-root": { borderRadius: 2 },
	"& .MuiInputBase-input": { fontWeight: 500 },
};

/* ─────────────────── Validation ─────────────────── */
const dateTimeInputFormat = "YYYY-MM-DDTHH:mm";

const getCurrentLocalDeadline = () => dayjs().format(dateTimeInputFormat);

const buildValidationSchema = (enforceFutureDeadline: boolean) =>
	yup.object().shape({
		title: yup.string().required("Title is required"),
		description: yup.string().required("Description is required"),
		deadline: yup
			.string()
			.required("Deadline is required")
			.test(
				"deadline-not-in-past",
				"Deadline must be now or later",
				(value) =>
					!enforceFutureDeadline ||
					!value ||
					!dayjs(value).isBefore(dayjs().startOf("minute")),
			),
		selectedGroups: yup
			.array()
			.of(
				yup.object().shape({
					Id: yup.number().required(),
					Name: yup.string().required(),
				}),
			)
			.min(1, "At least one responsible group must be selected")
			.required("At least one responsible group must be selected"),
		selectedUsers: yup
			.array()
			.of(
				yup.object().shape({
					Id: yup.number().required(),
					Name: yup.string().required(),
				}),
			)
			.notRequired(),
	});

/* ─────────────────── Component ─────────────────── */
export const AddNewTaskDialog: FC<AddNewTaskDialogProps> = ({
	open,
	handleClose,
	title,
	groupOptions,
	userOptions,
	initialData,
	systemOptions,
	enforceFutureDeadline = false,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { createTask, isSubmitting } = useCreateTask();
	const minimumDeadline = enforceFutureDeadline
		? getCurrentLocalDeadline()
		: undefined;
	const validationSchema = useMemo(
		() => buildValidationSchema(enforceFutureDeadline),
		[enforceFutureDeadline],
	);
	const sectionCardSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 1.5, md: 2 },
	};
	const initialDeadline = initialData?.deadline?.slice(0, 16);
	const fallbackDeadline =
		enforceFutureDeadline && initialDeadline
			? dayjs(initialDeadline).isBefore(dayjs().startOf("minute"))
				? getCurrentLocalDeadline()
				: initialDeadline
			: initialDeadline;

	const initialValues = useMemo<FormValues>(
		() => ({
			title: initialData?.title || "",
			description: initialData?.description || "",
			deadline: fallbackDeadline ?? getCurrentLocalDeadline(),
			showBefore: initialData?.showBefore || "AtOnce",
			frequency: initialData?.recurringFrequency || "Never",
			system: initialData?.system ?? systemOptions[0]?.Id ?? 0,
			handover: Boolean(initialData?.handover),
			shiftlog: Boolean(initialData?.shiftlog),
			tags: initialData?.tags || [],
			selectedGroups: initialData?.groupsResponsible?.length
				? groupOptions.filter((g) =>
						initialData.groupsResponsible!.includes(g.Id),
					)
				: [],
			selectedUsers: initialData?.usersResponsible?.length
				? userOptions.filter((u) =>
						initialData.usersResponsible!.includes(u.Id),
					)
				: [],
			fileName: "",
			file: null,
		}),
		[initialData, groupOptions, userOptions, systemOptions, fallbackDeadline],
	);

	const onSubmit = async (
		values: FormValues,
		{ setSubmitting }: FormikHelpers<FormValues>,
	) => {
		setSubmitting(true);
		try {
			// Match backend TaskSave / /api/Tasks field names and casing
			const payload: TaskSave = {
				Title: values.title,
				Description: values.description,
				Deadline: dayjs(values.deadline).utc().format(),
				ShowBefore: values.showBefore,
				// IMPORTANT: backend expects "ReccuringFrequency" (double 'c')
				ReccuringFrequency: values.frequency,
				SelectedSystem: values.system,
				Handover: values.handover ? 1 : 0,
				Shiftlog: values.shiftlog ? 1 : 0,
				ReccuringTemplateId: 0,
				Tags: values.tags.length ? values.tags : [],
				GroupsResponsible: values.selectedGroups.map((g) => g.Id),
				UsersResponsible: values.selectedUsers.map((u) => u.Id),
			};

			if (values.file) {
				payload.File = values.file;
				payload.FileName = values.file.name;
			}

			await createTask(payload);
			await AlertToast.success("Task created");
			handleClose();
		} catch (error) {
			console.error(error);
			await AlertToast.failed("Failed to save task");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<ErrorBoundary
			fallbackRender={({ error, resetErrorBoundary }) => (
				<ComponentError
					message={error instanceof Error ? error.message : "Unexpected error"}
					reset={resetErrorBoundary}
				/>
			)}
		>
			<Suspense fallback={<CircularProgress sx={{ m: 4 }} />}>
				<Dialog
					open={open}
					onClose={handleClose}
					fullWidth
					maxWidth="lg"
					fullScreen={isMobile}
					aria-labelledby="add-task-dialog-title"
					PaperProps={{
						sx: {
							...sharedTaskDialogPaperSx(theme, isMobile),
						},
					}}
				>
					{/* Dialog Header */}
					<DialogTitle
						id="add-task-dialog-title"
						sx={{
							...sharedTaskDialogHeaderSx(theme),
							pr: 7,
							pl: { xs: 2, md: 3 },
							py: 1.5,
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<Typography variant="h6" sx={{ fontWeight: 900 }}>
							<Translate String="AddNewTaskDialog.NewTask">New Task</Translate>{" "}
							<span style={{ opacity: 0.6 }}>·</span> {title}
						</Typography>
						<IconButton
							aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Ui.Components.AddNewTaskDialog.L311C19.Close", "Close")}
							onClick={handleClose}
							disabled={isSubmitting}
							size="small"
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</DialogTitle>

					{/* Content */}
					<DialogContent
						dividers
						sx={{
							...sharedTaskDialogContentSx(theme),
							p: { xs: 2, md: 3 },
							overflowY: "auto",
							WebkitOverflowScrolling: "touch",
							flex: 1,
							minHeight: 0,
						}}
					>
						<Formik
							initialValues={initialValues}
							validationSchema={validationSchema}
							onSubmit={onSubmit}
							enableReinitialize
						>
							{({
								values,
								handleChange,
								setFieldValue,
								setFieldTouched,
								isSubmitting,
								touched,
								errors,
							}) => (
								<Form>
									<Grid container spacing={2.25}>
										<Grid item xs={12}>
											<Paper variant="outlined" sx={sectionCardSx}>
												<Grid container spacing={2.25}>
													<Grid item xs={12} md={6}>
														<TextField
															fullWidth
															id="title"
															name="title"
															label={
																<Translate String="AddNewTaskDialog.Title">
																	Title
																</Translate>
															}
															value={values.title}
															onChange={handleChange}
															disabled={isSubmitting}
															error={Boolean(touched.title && errors.title)}
															helperText={
																touched.title
																	? toErrorText(errors.title)
																	: undefined
															}
															aria-required="true"
															aria-describedby="title-error-text"
															sx={roundedInput}
														/>
													</Grid>
													<Grid item xs={12} md={6}>
														<FormControl
															fullWidth
															disabled={isSubmitting}
															error={Boolean(touched.system && errors.system)}
															sx={roundedInput}
														>
															<InputLabel id="system-label">
																<Translate String="AddNewTaskDialog.System">
																	System
																</Translate>
															</InputLabel>
															<Select
																labelId="system-label"
																id="system"
																name="system"
																value={values.system}
																onChange={(e: SelectChangeEvent<number | string>) =>
																	handleChange(e)
																}
																label={
																	<Translate String="AddNewTaskDialog.System">
																		System
																	</Translate>
																}
																aria-required="true"
																aria-describedby="system-error-text"
																MenuProps={{
																	PaperProps: {
																		elevation: 3,
																		sx: { borderRadius: 2 },
																	},
																}}
															>
																{systemOptions.map((sys) => (
																	<MenuItem key={sys.Id} value={sys.Id}>
																		{sys.Name.trim()}
																	</MenuItem>
																))}
															</Select>
															{touched.system && errors.system && (
																<FormHelperText id="system-error-text">
																	{toErrorText(errors.system)}
																</FormHelperText>
															)}
														</FormControl>
													</Grid>
												</Grid>
											</Paper>
										</Grid>

										<Grid item xs={12}>
											<Paper variant="outlined" sx={sectionCardSx}>
												<Grid container spacing={2.25}>
													<Grid item xs={12} md={8}>
														<SATOSEditorField
															id="description"
															name="description"
															label="Description"
															disabled={isSubmitting}
															aria-required="true"
															aria-describedby="description-error-text"
															style={{ minWidth: "100%" }}
															imageUploadDisabled={true}
														/>
														{touched.description && errors.description && (
															<FormHelperText error sx={{ mt: 0.5 }}>
																{toErrorText(errors.description)}
															</FormHelperText>
														)}
													</Grid>
													<Grid item xs={12} md={4}>
														<FormControl
															fullWidth
															disabled={isSubmitting}
															error={Boolean(touched.tags && errors.tags)}
														>
															<CustomTextAreaArray
																name="tags"
																label="Tags"
																disabled={isSubmitting}
															/>
															{touched.tags && errors.tags && (
																<FormHelperText>
																	{toErrorText(errors.tags)}
																</FormHelperText>
															)}
														</FormControl>
														<Box mt={1}>
															<CustomCheckbox
																id="new-task-shiftlog"
																name="shiftlog"
																label="Shiftlog"
																disabled={isSubmitting}
															/>
														</Box>
														<Box mt={1}>
															<CustomCheckbox
																id="new-task-handover"
																name="handover"
																label="Handover"
																disabled={isSubmitting}
															/>
														</Box>
													</Grid>
												</Grid>
											</Paper>
										</Grid>

										<Grid item xs={12}>
											<Paper variant="outlined" sx={sectionCardSx}>
												<Grid container spacing={2.25}>
													<Grid item xs={12} md={4}>
														<TextField
															fullWidth
															id="deadline"
															name="deadline"
															label={
																<Translate String="AddNewTaskDialog.Deadline">
																	Deadline
																</Translate>
															}
															type="datetime-local"
															value={values.deadline}
															onChange={handleChange}
															InputLabelProps={{ shrink: true }}
															inputProps={
																minimumDeadline
																	? { min: minimumDeadline }
																	: undefined
															}
															disabled={isSubmitting}
															error={Boolean(touched.deadline && errors.deadline)}
															helperText={
																touched.deadline
																	? toErrorText(errors.deadline)
																	: undefined
															}
															aria-required="true"
															aria-describedby="deadline-error-text"
															sx={roundedInput}
														/>
													</Grid>
													<Grid item xs={12} md={4}>
														<FormControl
															fullWidth
															disabled={isSubmitting}
															error={Boolean(
																touched.frequency && errors.frequency,
															)}
															sx={roundedInput}
														>
															<InputLabel id="frequency-label">
																<Translate String="Task.AddNewTaskDialog.Repeat">
																	Repeat
																</Translate>
															</InputLabel>
															<Select
																labelId="frequency-label"
																id="frequency"
																name="frequency"
																value={values.frequency}
																onChange={(e: SelectChangeEvent<string>) =>
																	handleChange(e)
																}
																label={
																	<Translate String="AddNewTaskDialog.Repeat">
																		Repeat
																	</Translate>
																}
																aria-describedby="frequency-error-text"
																MenuProps={{
																	PaperProps: {
																		elevation: 3,
																		sx: { borderRadius: 2 },
																	},
																}}
															>
																{Object.entries(RecurringFrequencyMap).map(
																	([label, val]) => (
																		<MenuItem key={val} value={val}>
																			{label}
																		</MenuItem>
																	),
																)}
															</Select>
															{touched.frequency && errors.frequency && (
																<FormHelperText id="frequency-error-text">
																	{toErrorText(errors.frequency)}
																</FormHelperText>
															)}
														</FormControl>
													</Grid>
													<Grid item xs={12} md={4}>
														<FormControl
															fullWidth
															disabled={isSubmitting}
															error={Boolean(
																touched.showBefore && errors.showBefore,
															)}
															sx={roundedInput}
														>
															<InputLabel id="showBefore-label">
																<Translate String="AddNewTaskDialog.ShowBefore">
																	Show Before
																</Translate>
															</InputLabel>
															<Select
																labelId="showBefore-label"
																id="showBefore"
																name="showBefore"
																value={values.showBefore}
																onChange={(e: SelectChangeEvent<string>) =>
																	handleChange(e)
																}
																label={
																	<Translate String="AddNewTaskDialog.ShowBefore">
																		Show Before
																	</Translate>
																}
																aria-describedby="showBefore-error-text"
																MenuProps={{
																	PaperProps: {
																		elevation: 3,
																		sx: { borderRadius: 2 },
																	},
																}}
															>
																{Object.entries(RecurringShowBeforeMap).map(
																	([label, val]) => (
																		<MenuItem key={val} value={val}>
																			{label}
																		</MenuItem>
																	),
																)}
															</Select>
															{touched.showBefore && errors.showBefore && (
																<FormHelperText id="showBefore-error-text">
																	{toErrorText(errors.showBefore)}
																</FormHelperText>
															)}
														</FormControl>
													</Grid>
												</Grid>
											</Paper>
										</Grid>

										<Grid item xs={12}>
											<Paper variant="outlined" sx={sectionCardSx}>
												<Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
													<Translate String="AddNewTaskDialog.Attachment">
														Attachment
													</Translate>
												</Typography>
												<TaskAttachmentDropzone
													fileName={values.fileName}
													disabled={isSubmitting}
													onFileSelected={(file) => {
														void setFieldValue("file", file);
														void setFieldValue("fileName", file.name);
													}}
													onClear={() => {
														void setFieldValue("file", null);
														void setFieldValue("fileName", "");
													}}
												/>
											</Paper>
										</Grid>

										<Grid item xs={12}>
											<Paper variant="outlined" sx={sectionCardSx}>
												<Grid container spacing={2.25}>
													<Grid item xs={12} md={6}>
											<FormControl
												fullWidth
												disabled={isSubmitting}
												error={Boolean(
													touched.selectedGroups && errors.selectedGroups,
												)}
												sx={roundedInput}
											>
												<InputLabel id="groups-label">
													<Translate String="AddNewTaskDialog.ResponsibleGroups">
														Responsible Groups
													</Translate>
												</InputLabel>
												<Select
													labelId="groups-label"
													id="selectedGroups"
													name="selectedGroups"
													multiple
													value={values.selectedGroups.map((g) => g.Id)}
													onChange={(e: SelectChangeEvent<number[]>) => {
														const numericIds = toIdList(e.target.value);
														const selectedOpts = numericIds
															.map(
																(id) => groupOptions.find((g) => g.Id === id)!,
															)
															.filter(Boolean);
														void setFieldValue("selectedGroups", selectedOpts);
													}}
													onBlur={() => {
														void setFieldTouched("selectedGroups", true);
													}}
													label={
														<Translate String="AddNewTaskDialog.ResponsibleGroups">
															Responsible Groups
														</Translate>
													}
													aria-required="true"
													aria-describedby="groups-error-text"
													renderValue={(selected) => (
														<Box
															sx={{
																display: "flex",
																flexWrap: "wrap",
																gap: 0.5,
															}}
														>
															{toIdList(selected).map((id) => {
																const item = groupOptions.find(
																	(g) => g.Id === id,
																);
																return item ? (
																	<Chip
																		key={id}
																		label={item.Name}
																		size="small"
																		sx={{
																			bgcolor: COL.chipBg,
																			color: COL.chipText,
																			fontWeight: 700,
																		}}
																	/>
																) : null;
															})}
														</Box>
													)}
													MenuProps={{
														PaperProps: {
															elevation: 3,
															sx: { borderRadius: 2 },
														},
													}}
												>
													{groupOptions.map((opt) => (
														<MenuItem key={opt.Id} value={opt.Id}>
															{opt.Name}
														</MenuItem>
													))}
												</Select>
												{touched.selectedGroups && errors.selectedGroups && (
													<FormHelperText
														id="groups-error-text"
														sx={{ color: "error.main" }}
													>
														{toErrorText(errors.selectedGroups)}
													</FormHelperText>
												)}
											</FormControl>
													</Grid>
													<Grid item xs={12} md={6}>
											<FormControl
												fullWidth
												disabled={isSubmitting}
												error={Boolean(
													touched.selectedUsers && errors.selectedUsers,
												)}
												sx={roundedInput}
											>
												<InputLabel id="users-label">
													<Translate String="AddNewTaskDialog.ResponsibleUsers">
														Responsible Users
													</Translate>
												</InputLabel>
												<Select
													labelId="users-label"
													id="selectedUsers"
													name="selectedUsers"
													multiple
													value={values.selectedUsers.map((u) => u.Id)}
													onChange={(e: SelectChangeEvent<number[]>) => {
														const numericIds = toIdList(e.target.value);
														const selectedOpts = numericIds
															.map((id) => userOptions.find((u) => u.Id === id))
															.filter((u): u is Option => !!u);

														void setFieldValue("selectedUsers", selectedOpts);
													}}
													label={
														<Translate String="AddNewTaskDialog.ResponsibleUsers">
															Responsible Users
														</Translate>
													}
													aria-required="false"
													aria-describedby="users-error-text"
													renderValue={(selected) => (
														<Box
															sx={{
																display: "flex",
																flexWrap: "wrap",
																gap: 0.5,
															}}
														>
															{toIdList(selected).map((id) => {
																const item = userOptions.find(
																	(u) => u.Id === id,
																);
																return item ? (
																	<Chip
																		key={id}
																		label={item.Name}
																		size="small"
																		sx={{ bgcolor: "#F1F5F9", fontWeight: 700 }}
																	/>
																) : null;
															})}
														</Box>
													)}
													MenuProps={{
														PaperProps: {
															elevation: 3,
															sx: { borderRadius: 2 },
														},
													}}
												>
													{userOptions.map((opt) => (
														<MenuItem key={opt.Id} value={opt.Id}>
															{opt.Name}
														</MenuItem>
													))}
												</Select>

												{touched.selectedUsers && errors.selectedUsers && (
													<FormHelperText id="users-error-text">
														{toErrorText(errors.selectedUsers)}
													</FormHelperText>
												)}
											</FormControl>
													</Grid>
												</Grid>
											</Paper>
										</Grid>
									</Grid>

									{/* Footer Actions */}
									<DialogActions
										sx={{
											...sharedTaskDialogActionsSx(theme, false),
											mt: 3,
											px: 0,
											pt: 2,
										}}
									>
										<Box sx={{ flex: 1 }} />
										<CloseActionButton
											onClick={handleClose}
											startIcon={<CloseIcon />}
											disabled={isSubmitting}
											sx={{
												borderRadius: 2,
												textTransform: "none",
												fontWeight: 800,
											}}
										>
											<Translate String="AddNewTaskDialog.Cancel">
												Cancel
											</Translate>
										</CloseActionButton>
										<SaveActionButton
											type="submit"
											startIcon={
												isSubmitting ? (
													<CircularProgress size={16} color="inherit" />
												) : (
													<SaveIcon />
												)
											}
											disabled={isSubmitting}
											sx={{
												borderRadius: 2,
												textTransform: "none",
												fontWeight: 900,
											}}
										>
											{isSubmitting ? (
												<Translate String="AddNewTaskDialog.Saving">
													Saving…
												</Translate>
											) : (
												<Translate String="AddNewTaskDialog.Save">
													Save
												</Translate>
											)}
										</SaveActionButton>
									</DialogActions>
								</Form>
							)}
						</Formik>
					</DialogContent>
				</Dialog>
			</Suspense>
		</ErrorBoundary>
	);
};

export default AddNewTaskDialog;

/* ─────────────────── Recurring options (left as-is) ─────────────────── */
export const RecurringFrequencyMap: Record<string, string> = {
	Never: "Never",
	Daily: "Daily",
	SecondDay: "SecondDay",
	"Third Day": "ThirdDay",
	"Twice Weekly": "TwiceWeekly",
	Weekly: "Weekly",
	"Twelfth Hour": "TwelfthHour",
	"Second Week": "SecondWeek",
	Monthly: "Monthly",
	"Sixth Week": "SixthWeek",
	"Second Month": "SecondMonth",
	Quarterly: "Quarterly",
	"Twice Yearly": "TwiceYearly",
	Yearly: "Yearly",
};

export const RecurringShowBeforeMap: Record<string, string> = {
	"At once": "AtOnce",
	"On due": "OnDue",
	"1 hour before": "OneHour",
	"2 hours before": "TwoHours",
	"3 hours before": "ThreeHours",
	"6 hours before": "SixHours",
	"12 hours before": "TwelveHours",
	"1 day before": "OneDay",
	"2 days before": "TwoDays",
};
